import React, { useCallback, useEffect, useState } from "react";
import moment from "moment";
import SimpleBar from "simplebar-react";
import {
  Card,
  CardBody,
  Col,
  Container,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  Row,
  UncontrolledDropdown,
} from "reactstrap";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";

import BreadCrumb from "Components/Common/BreadCrumb";
import DeleteModal from "Components/Common/DeleteModal";
import FileIcon from "Components/Common/FileIcon";
import Permission from "Components/Common/Permission";
import { usePageActivity } from "Components/Hooks/UsePageActivity";
import { APP_NAME_SHORT, CARD_MIN_HEIGHT } from "common/constants";
import { normalize } from "common/lib/normalize";
import {
  deleteAssessment,
  getAssessment,
} from "common/services/assessments.service";
import CapacityAssessmentRating from "../CapacityAssessments/ViewCapacityAssessment/CapacityAssessmentRating";
import RiskAssessmentRating from "../RiskAssessments/RiskAssessmentRating";
import UpdateAssessment from "./UpdateAssessment";

const detailItems = [
  { label: "Assessment Type", key: "assessmentType", value: (data: any) => data?.assessmentType?.name || "-" },
  { label: "Title", key: "title", value: (data: any) => data?.title || "-" },
  { label: "Agency", key: "agency", value: (data: any) => data?.agency?.name || "-" },
  { label: "Serial Number", key: "serialNumber", value: (data: any) => data?.serialNumber || "-" },
  { label: "Location", key: "location", value: (data: any) => data?.location || "-" },
  { label: "Report Date", key: "reportDate", value: (data: any) => (data?.reportDate ? moment(data.reportDate).format("DD MMM, Y") : "-") },
  { label: "Verification Date", key: "verificationDate", value: (data: any) => (data?.verificationDate ? moment(data.verificationDate).format("DD MMM, Y") : "-") },
  { label: "Sensitivity", key: "sensitivity", value: (data: any) => data?.sensitivity || "-" },
  { label: "Classification Level", key: "classificationLevel", value: (data: any) => data?.classificationLevel || "-" },
  {
    label: "Capacity Assessment Type",
    key: "capacityAssessmentType",
    value: (data: any) => data?.capacityAssessmentType?.name || "-",
  },
  {
    label: "No. of Associated Entities Screened",
    key: "noOfAssociatedEntitiesScreened",
    value: (data: any) => data?.noOfAssociatedEntitiesScreened ?? "-",
  },
  {
    label: "No. of Individuals Screened",
    key: "noofindividualsScreened",
    value: (data: any) => data?.noofindividualsScreened ?? "-",
  },
  {
    label: "Vetting Recommendation",
    key: "vettingEligibilityRecommendation",
    value: (data: any) => data?.vettingEligibilityRecommendation || "-",
  },
];

const riskItems = [
  { label: "Programme Risk", key: "programmeRisk" },
  { label: "Diversion Risk", key: "diversionRisk" },
  { label: "Fiduciary Risk", key: "fiduciaryRisk" },
  { label: "Integrity Risk", key: "integrityRisk" },
  { label: "Fraud Risk", key: "fraudRisk" },
  { label: "Reputation Risk", key: "reputationRisk" },
  { label: "Human Rights Risk", key: "humanRightsRisk" },
  { label: "Terrorism Risk", key: "terrorismRisk" },
  { label: "Corruption Risk", key: "corruptionRisk" },
  { label: "Obstruction Risk", key: "obstructionRisk" },
  { label: "Theft Risk", key: "theftRisk" },
  { label: "Partner Management Risk", key: "partnerManagementRisk" },
  { label: "Community Relations Risk", key: "communityRelationsRisk" },
];

const ViewAssessment = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { id: itemId } = useParams();

  usePageActivity(location.pathname, "View Assessment", itemId);

  const [data, setData] = useState<any>({});
  const [isUpdateModalOpen, setIsUpdateModalOpen] = useState(false);
  const [deleteModal, setDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const response: any = await getAssessment(itemId, {});
      setData(normalize(response.data));
    } catch (error) {
    }
  }, [itemId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleDelete = async () => {
    try {
      setDeleting(true);
      await deleteAssessment(data);
      navigate("/assessments/all-assessments");
      setDeleteModal(false);
    } catch (error) {
      toast.error("Error while deleting assessment", {
        autoClose: 1000,
      });
    } finally {
      setDeleting(false);
    }
  };

  document.title = `Assessment - ${APP_NAME_SHORT}`;

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb
          title="Assessment Details"
          back
          pageTitles={[
            { title: "Dashboard", link: "/dashboard" },
            { title: "All Assessments", link: "/assessments/all-assessments" },
          ]}
        />

        <Row>
          <Col lg={12}>
            <Card className="rounded">
              <div className="bg-secondary-subtle rounded">
                <CardBody className="pb-4">
                  <Row>
                    <div className="col-md">
                      <Row className="align-items-center">
                        <div className="col-md-auto">
                          <div className="avatar-md mb-md-0 mb-4 img-thumbnail rounded-circle">
                            <div className="avatar-title bg-secondary-subtle rounded-circle fs-24">
                              <i className="ri-file-paper-2-line text-secondary"></i>
                            </div>
                          </div>
                        </div>
                        <div className="col-md">
                          <h4 className="fw-semibold">{data?.title || "Assessment"}</h4>
                          <div className="hstack gap-3 flex-wrap">
                            <div className="text-muted text-capitalize">
                              <i className="ri-shield-check-line align-bottom me-1"></i>
                              {data?.assessmentType?.name || "-"}
                            </div>
                            <div className="text-muted">
                              <i className="ri-building-line align-bottom me-1"></i>
                              {data?.contractor?.contractorName || "-"}
                            </div>
                          </div>
                        </div>
                      </Row>
                    </div>
                    <div className="col-md-auto">
                      <div className="hstack gap-1 flex-wrap">
                        <UncontrolledDropdown className="float-end">
                          <DropdownToggle
                            tag="a"
                            className="btn py-0 fs-13 material-shadow-none align-middle text-primary"
                            role="button"
                          >
                            Actions
                            <i className="ri-arrow-down-s-fill fs-16 ms-1"></i>
                          </DropdownToggle>
                          <DropdownMenu className="dropdown-menu-end">
                            <Permission action="update" resource="assessment">
                              <DropdownItem
                                href="#"
                                onClick={(e: any) => {
                                  e.preventDefault();
                                  setIsUpdateModalOpen(true);
                                }}
                              >
                                <i className="ri-pencil-fill align-bottom me-2 text-muted"></i>
                                Edit
                              </DropdownItem>
                            </Permission>
                            <Permission action="delete" resource="assessment">
                              <DropdownItem
                                href="#"
                                onClick={(e: any) => {
                                  e.preventDefault();
                                  setDeleteModal(true);
                                }}
                              >
                                <i className="ri-delete-bin-fill align-bottom me-2 text-muted"></i>
                                Delete
                              </DropdownItem>
                            </Permission>
                          </DropdownMenu>
                        </UncontrolledDropdown>
                      </div>
                    </div>
                  </Row>
                </CardBody>
              </div>
            </Card>
          </Col>
        </Row>

        <Row>
          <Col xxl={9}>
            <Card>
              <CardBody>
                <div className="text-muted">
                  <h6 className="mb-2 fw-semibold text-uppercase">Assessment Details</h6>
                </div>
              </CardBody>
              <CardBody style={{ minHeight: CARD_MIN_HEIGHT }}>
                <div className="fs-13">
                  <Row className="px-1 mb-2">
                    <Col lg={3} sm={3}>
                      <div className="fw-medium">Contractor:</div>
                    </Col>
                    <Col lg={9} sm={9}>
                      {data?.contractor?.id ? (
                        <Link to={`/contractors/${data.contractor.id}`} className="text-truncate d-block">
                          <i className="ri-building-line align-bottom me-1"></i>
                          {data.contractor.contractorName}
                        </Link>
                      ) : (
                        "-"
                      )}
                    </Col>
                  </Row>

                  {detailItems.map((item) => (
                    <Row className="px-1 mb-2" key={item.key}>
                      <Col lg={3} sm={3}>
                        <div className="fw-medium">{item.label}:</div>
                      </Col>
                      <Col lg={9} sm={9}>
                        {item.value(data)}
                      </Col>
                    </Row>
                  ))}

                  <Row className="px-1 mb-2">
                    <Col lg={3} sm={3}>
                      <div className="fw-medium">Conducted By:</div>
                    </Col>
                    <Col lg={9} sm={9}>
                      <div className="mb-1">
                        <span className="fw-medium me-2">User / Staff:</span>
                        {`${data?.conductedByUser?.firstName || ""} ${
                          data?.conductedByUser?.lastName || ""
                        }`.trim() || "-"}
                      </div>
                      <div>
                        <span className="fw-medium me-2">Contractor / Audit Firm:</span>
                        {data?.conductedByContractor?.contractorName || "-"}
                      </div>
                    </Col>
                  </Row>

                  <Row className="px-1 mb-2">
                    <Col lg={3} sm={3}>
                      <div className="fw-medium">Summary:</div>
                    </Col>
                    <Col lg={9} sm={9}>
                      {data?.summary || "-"}
                    </Col>
                  </Row>

                  <div className="pt-3 border-top border-top-dashed mt-4">
                    <Row className="gy-3">
                      <Col lg={6}>
                        <div>
                          <h6 className="mb-3 fw-semibold text-uppercase">Overall Capacity Rating</h6>
                          {data?.overallRating?.name ? (
                            <CapacityAssessmentRating rating={data.overallRating.name} solid />
                          ) : (
                            <span>-</span>
                          )}
                        </div>
                      </Col>
                      <Col lg={6}>
                        <div>
                          <h6 className="mb-3 fw-semibold text-uppercase">Overall Risk Rating</h6>
                          {data?.overallRiskRating?.name ? (
                            <RiskAssessmentRating rating={data.overallRiskRating.name} solid />
                          ) : (
                            <span>-</span>
                          )}
                        </div>
                      </Col>
                    </Row>
                  </div>

                  <div className="pt-3 border-top border-top-dashed mt-4">
                    <h6 className="mb-3 fw-semibold text-uppercase">Detailed Risk Profile</h6>
                    <Row>
                      {riskItems.map((item) => (
                        <Col md={4} className="mb-3" key={item.key}>
                          <div className="fw-medium mb-1">{item.label}</div>
                          {data?.[item.key]?.name ? (
                            <RiskAssessmentRating rating={data[item.key].name} solid={false} fs="fs-12" />
                          ) : (
                            <span>-</span>
                          )}
                        </Col>
                      ))}
                    </Row>
                  </div>

                  <div className="pt-3 border-top border-top-dashed mt-4">
                    <h6 className="mb-3 fw-semibold text-uppercase">Documents & Resources</h6>
                    <Row className="g-3">
                      {data?.reportFile?.length ? (
                        data.reportFile.map((doc: any, index: number) => (
                          <Col xxl={6} lg={6} key={index}>
                            <div className="border rounded border-dashed p-2">
                              <div className="d-flex align-items-center">
                                <div className="flex-shrink-0 me-3">
                                  <div className="avatar-sm">
                                    <div className="avatar-title bg-light text-secondary rounded fs-24">
                                      <FileIcon fileName={doc?.name} />
                                    </div>
                                  </div>
                                </div>
                                <div className="flex-grow-1 overflow-hidden">
                                  <h5 className="fs-13 mb-1">
                                    <Link
                                      to={`${process.env.REACT_APP_FILES_URL}${doc?.url}`}
                                      className="text-body text-truncate d-block"
                                      target="_blank"
                                    >
                                      {doc?.name}
                                    </Link>
                                  </h5>
                                  <div>{doc?.formattedSize}</div>
                                </div>
                                <div className="flex-shrink-0 ms-2">
                                  <Link
                                    className="btn btn-icon text-muted btn-sm fs-18"
                                    to={`${process.env.REACT_APP_FILES_URL}${doc?.url}`}
                                    target="_blank"
                                  >
                                    <i className="ri-download-2-line"></i>
                                  </Link>
                                </div>
                              </div>
                            </div>
                          </Col>
                        ))
                      ) : (
                        <Col lg={12}>
                          <span className="text-muted">No files attached.</span>
                        </Col>
                      )}
                    </Row>
                  </div>
                </div>
              </CardBody>
            </Card>
          </Col>

          <Col xxl={3} className="mb-4">
            <Card className="h-100">
              <CardBody className="p-0">
                <div className="p-3">
                  <h6 className="text-muted mb-0 text-uppercase fw-semibold">Activity</h6>
                </div>
                <SimpleBar style={{ maxHeight: "410px" }} className="p-3 pt-0">
                  <div className="acitivity-timeline acitivity-main">
                    <div className="acitivity-item d-flex">
                      <div className="flex-shrink-0 avatar-xs acitivity-avatar">
                        <div className="avatar-title bg-warning-subtle text-warning rounded-circle">
                          <i className="ri-time-line"></i>
                        </div>
                      </div>
                      <div className="flex-grow-1 ms-3">
                        <h6 className="mb-1 lh-base">Last Updated</h6>
                        <p className="text-muted mb-1">
                          {`By ${data?.createdby?.firstName || ""} ${data?.createdby?.lastName || ""} - ${data?.createdby?.agency?.name || ""}`}
                        </p>
                        <small className="mb-0 text-muted">
                          {data?.updatedAt ? moment(new Date(data.updatedAt)).format("DD MMM, Y") : "-"}
                        </small>
                      </div>
                    </div>
                    <div className="acitivity-item py-3 d-flex">
                      <div className="flex-shrink-0 avatar-xs acitivity-avatar">
                        <div className="avatar-title bg-primary-subtle text-primary rounded-circle">
                          <i className="ri-calendar-line"></i>
                        </div>
                      </div>
                      <div className="flex-grow-1 ms-3">
                        <h6 className="mb-1 lh-base">Created</h6>
                        <p className="text-muted mb-1">
                          {`By ${data?.createdby?.firstName || ""} ${data?.createdby?.lastName || ""} - ${data?.createdby?.agency?.name || ""}`}
                        </p>
                        <p className="mb-0 text-muted">
                          <small>
                            {data?.createdAt ? moment(new Date(data.createdAt)).format("DD MMM, Y") : "-"}
                          </small>
                        </p>
                      </div>
                    </div>
                  </div>
                </SimpleBar>
              </CardBody>
            </Card>
          </Col>
        </Row>

        <UpdateAssessment
          toggle={() => setIsUpdateModalOpen((state) => !state)}
          isModalOpen={isUpdateModalOpen}
          fetchUpdatedData={fetchData}
          selectedRecord={data}
        />
        <DeleteModal
          show={deleteModal}
          onDeleteClick={() => handleDelete()}
          onCloseClick={() => setDeleteModal(false)}
          loading={deleting}
        />
      </Container>
    </div>
  );
};

export default ViewAssessment;
